// XP Gamification Service
// Handles all XP-related API calls

import { supabase } from './supabaseClient';
import { getApiBaseUrl } from './config/apiConfig';

const API_BASE_URL = `${getApiBaseUrl()}/api`;

// Helper to get auth token
async function getAuthToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token;
}

/**
 * Get student's XP summary
 * @param {string} studentId - UUID of student
 * @returns {Promise} { total_xp, current_level, xp_to_next_level, recent_transactions }
 */
export async function getXPSummary(studentId) {
  try {
    const token = await getAuthToken();
    if (!token) {
      return { data: null, error: 'Not authenticated' };
    }

    const response = await fetch(`${API_BASE_URL}/xp/summary?studentId=${studentId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { data: null, error: errorData.error || 'Failed to fetch XP summary' };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error('XP summary error:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Get student's XP transaction history
 * @param {string} studentId - UUID of student
 * @param {number} limit - Number of transactions to fetch (default: 20)
 * @param {number} offset - Offset for pagination (default: 0)
 * @returns {Promise} { transactions[], total, student }
 */
export async function getXPTransactions(studentId, limit = 20, offset = 0) {
  try {
    const token = await getAuthToken();
    if (!token) {
      return { data: null, error: 'Not authenticated' };
    }

    const response = await fetch(
      `${API_BASE_URL}/xp/transactions?studentId=${studentId}&limit=${limit}&offset=${offset}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      return { data: null, error: errorData.error || 'Failed to fetch transactions' };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error('XP transactions error:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Get student's referral code
 * @returns {Promise} { referral_code }
 */
export async function getReferralCode() {
  try {
    const token = await getAuthToken();
    if (!token) {
      return { data: null, error: 'Not authenticated' };
    }

    const response = await fetch(`${API_BASE_URL}/referral/code`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { data: null, error: errorData.error || 'Failed to fetch referral code' };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error('Referral code error:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Get student's referral history
 * @returns {Promise} { referrals[], total }
 */
export async function getMyReferrals() {
  try {
    const token = await getAuthToken();
    if (!token) {
      return { data: null, error: 'Not authenticated' };
    }

    const response = await fetch(`${API_BASE_URL}/referral/my-referrals`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { data: null, error: errorData.error || 'Failed to fetch referrals' };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error('My referrals error:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Validate a referral code
 * @param {string} code - Referral code to validate
 * @returns {Promise} { valid, referrer_name, referrer_email }
 */
export async function validateReferralCode(code) {
  try {
    const response = await fetch(`${API_BASE_URL}/referral/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ code })
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { data: null, error: errorData.error || 'Invalid referral code' };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error('Validate referral error:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Complete referral after signup
 * @param {string} referralCode - Referral code used
 * @param {string} newUserEmail - Email of new user
 * @param {string} newUserId - UUID of new user
 * @returns {Promise} { success, referral, xp_awarded }
 */
export async function completeReferralSignup(referralCode, newUserEmail, newUserId) {
  try {
    const response = await fetch(`${API_BASE_URL}/referral/complete-signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        referralCode,
        newUserEmail,
        newUserId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { data: null, error: errorData.error || 'Failed to complete referral' };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error('Complete referral error:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Award signup bonus to new student
 * @param {string} studentId - UUID of student
 * @param {string} studentEmail - Email of student
 * @returns {Promise} { success, xp_awarded, transaction, student }
 */
export async function awardSignupBonus(studentId, studentEmail) {
  try {
    console.log('🔑 Getting auth token...');
    const token = await getAuthToken();
    if (!token) {
      console.error('❌ No auth token available');
      return { data: null, error: 'Not authenticated' };
    }
    console.log('✅ Auth token obtained');

    console.log('📡 Calling signup bonus API:', `${API_BASE_URL}/xp/signup-bonus`);
    const response = await fetch(`${API_BASE_URL}/xp/signup-bonus`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        studentId,
        studentEmail
      })
    });

    console.log('📬 API response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ API error response:', errorData);
      return { data: null, error: errorData.error || 'Failed to award signup bonus' };
    }

    const data = await response.json();
    console.log('✅ Signup bonus API success:', data);
    return { data, error: null };
  } catch (error) {
    console.error('❌ Signup bonus exception:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Award profile completion bonus to student
 * @param {string} studentId - UUID of student
 * @param {string} studentEmail - Email of student
 * @returns {Promise} { success, xp_awarded, transaction, student }
 */
export async function awardProfileCompletionBonus(studentId, studentEmail) {
  try {
    console.log('🎯 Getting auth token for profile completion bonus...');
    const token = await getAuthToken();
    if (!token) {
      console.error('❌ No auth token available');
      return { data: null, error: 'Not authenticated' };
    }
    console.log('✅ Auth token obtained');

    console.log('📡 Calling profile completion bonus API:', `${API_BASE_URL}/xp/profile-completion-bonus`);
    const response = await fetch(`${API_BASE_URL}/xp/profile-completion-bonus`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        studentId,
        studentEmail
      })
    });

    console.log('📬 API response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ API error response:', errorData);
      return { data: null, error: errorData.error || 'Failed to award profile completion bonus' };
    }

    const data = await response.json();
    console.log('✅ Profile completion bonus API success:', data);
    return { data, error: null };
  } catch (error) {
    console.error('❌ Profile completion bonus exception:', error);
    return { data: null, error: error.message };
  }
}
